import { useState } from "react";
import { Menu, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function BankingApp() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("demo@usbank.com");
  const [password, setPassword] = useState("demo123");
  const [showMenu, setShowMenu] = useState(false);

  const handleLogin = () => {
    if (username && password) {
      setIsLoggedIn(true);
      setShowMenu(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setShowMenu(false);
    setUsername("demo@usbank.com");
    setPassword("demo123");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleLogin();
  };

  if (!isLoggedIn) {
    return (
      <div className="w-full max-w-sm mx-auto h-screen bg-zinc-950 text-white flex flex-col p-6 border-x border-zinc-800">
        {/* Logo */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-3xl font-bold">
            <span className="text-blue-600">U.S.</span>
            <span>BANK</span>
          </div>
          <p className="text-zinc-400 mt-2">Sign in to your account</p>
        </div>

        {/* Login Form */}
        <div className="mt-12 space-y-6">
          <div>
            <label className="text-sm text-zinc-400 block mb-1">
              Username / Email
            </label>
            <Input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={handleKeyPress}
              className="bg-zinc-900 border-zinc-700 text-white h-12 rounded-2xl px-5"
            />
          </div>

          <div>
            <label className="text-sm text-zinc-400 block mb-1">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              className="bg-zinc-900 border-zinc-700 text-white h-12 rounded-2xl px-5"
            />
          </div>

          <Button
            onClick={handleLogin}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 rounded-2xl text-lg"
          >
            Sign In
          </Button>

          <div className="flex justify-between text-sm text-blue-500">
            <a href="#" className="hover:underline">
              Forgot password?
            </a>
            <a href="#" className="hover:underline">
              Enroll now
            </a>
          </div>
        </div>

        <div className="mt-auto text-center text-xs text-zinc-500">
          Secure login powered by U.S. Bank
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm mx-auto h-screen bg-zinc-950 text-white flex flex-col border-x border-zinc-800 overflow-hidden">
      {/* Header */}
      <div className="bg-[#0033a0] px-6 pt-12 pb-6 relative">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-sm opacity-75">Good morning, Alex</div>
            <div className="text-2xl font-semibold">Welcome back</div>
          </div>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="w-10 h-10 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center hover:bg-white/30 transition"
          >
            <Menu className="text-xl" size={20} />
          </button>
        </div>

        {/* Dropdown Menu */}
        {showMenu && (
          <div className="absolute top-20 right-6 bg-zinc-900 rounded-2xl shadow-xl z-50 min-w-40">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-4 py-3 hover:bg-zinc-800 transition rounded-2xl text-red-400 first:rounded-t-2xl"
            >
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          </div>
        )}
      </div>

      {/* Balance Card */}
      <div className="px-6 -mt-6 relative z-10">
        <div className="bg-white text-zinc-900 rounded-3xl p-6 shadow-2xl hover:shadow-3xl transition">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-sm text-zinc-500">Available Balance</div>
              <div className="text-4xl font-bold mt-1">$12,458.73</div>
            </div>
            <div className="text-right">
              <div className="bg-green-100 text-green-700 text-xs px-3 py-1 rounded-full font-medium">
                +2.4%
              </div>
              <div className="text-xs text-zinc-500 mt-4">Checking •••• 4281</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-8">
            <button className="flex flex-col items-center gap-1 text-xs hover:opacity-70 transition">
              <div className="w-12 h-12 bg-zinc-100 rounded-2xl flex items-center justify-center text-2xl">
                ↗
              </div>
              <span>Transfer</span>
            </button>
            <button className="flex flex-col items-center gap-1 text-xs hover:opacity-70 transition">
              <div className="w-12 h-12 bg-zinc-100 rounded-2xl flex items-center justify-center text-2xl">
                ↓
              </div>
              <span>Deposit</span>
            </button>
            <button className="flex flex-col items-center gap-1 text-xs hover:opacity-70 transition">
              <div className="w-12 h-12 bg-zinc-100 rounded-2xl flex items-center justify-center text-2xl">
                💸
              </div>
              <span>Pay Bill</span>
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="px-6 mt-8 flex gap-4 overflow-x-auto pb-4">
        <div className="bg-zinc-900 rounded-3xl p-5 min-w-[160px] hover:bg-zinc-800 transition">
          <div className="text-emerald-400 text-sm">Savings</div>
          <div className="text-3xl font-bold mt-2">$8,742</div>
        </div>
        <div className="bg-zinc-900 rounded-3xl p-5 min-w-[160px] hover:bg-zinc-800 transition">
          <div className="text-amber-400 text-sm">Credit Card</div>
          <div className="text-3xl font-bold mt-2">$1,239</div>
          <div className="text-xs text-zinc-500">Due in 12 days</div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="flex-1 px-6 pt-4 overflow-auto">
        <div className="flex justify-between items-center mb-4">
          <div className="font-semibold">Recent Activity</div>
          <a href="#" className="text-blue-500 text-sm hover:underline">
            View all
          </a>
        </div>

        <div className="space-y-6">
          <div className="flex justify-between items-center hover:bg-zinc-900 p-3 rounded-xl transition">
            <div className="flex gap-4 flex-1">
              <div className="w-10 h-10 bg-zinc-800 rounded-2xl flex items-center justify-center flex-shrink-0">
                ☕
              </div>
              <div>
                <div className="font-medium">Starbucks</div>
                <div className="text-xs text-zinc-500">Today • Debit</div>
              </div>
            </div>
            <div className="text-right font-medium text-red-400">-$6.85</div>
          </div>

          <div className="flex justify-between items-center hover:bg-zinc-900 p-3 rounded-xl transition">
            <div className="flex gap-4 flex-1">
              <div className="w-10 h-10 bg-zinc-800 rounded-2xl flex items-center justify-center flex-shrink-0">
                💰
              </div>
              <div>
                <div className="font-medium">Direct Deposit - Acme Corp</div>
                <div className="text-xs text-zinc-500">Yesterday • Credit</div>
              </div>
            </div>
            <div className="text-right font-medium text-green-400">+$2,450.00</div>
          </div>

          <div className="flex justify-between items-center hover:bg-zinc-900 p-3 rounded-xl transition">
            <div className="flex gap-4 flex-1">
              <div className="w-10 h-10 bg-zinc-800 rounded-2xl flex items-center justify-center flex-shrink-0">
                🛒
              </div>
              <div>
                <div className="font-medium">Amazon Purchase</div>
                <div className="text-xs text-zinc-500">2 days ago • Debit</div>
              </div>
            </div>
            <div className="text-right font-medium text-red-400">-$89.99</div>
          </div>

          <div className="flex justify-between items-center hover:bg-zinc-900 p-3 rounded-xl transition">
            <div className="flex gap-4 flex-1">
              <div className="w-10 h-10 bg-zinc-800 rounded-2xl flex items-center justify-center flex-shrink-0">
                🎬
              </div>
              <div>
                <div className="font-medium">Netflix Subscription</div>
                <div className="text-xs text-zinc-500">3 days ago • Debit</div>
              </div>
            </div>
            <div className="text-right font-medium text-red-400">-$15.99</div>
          </div>
        </div>
      </div>
    </div>
  );
}
